import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { User } from 'services/login/types';
import { getApiBaseUrl } from 'utils/user';
import {
  ConnectionInfos,
  OtpRequestInfos,
  OtpVerifyInfos,
} from './types';

export const apiLogin = createApi({
  reducerPath: 'loginApi',
  tagTypes: ['Login'],
  baseQuery: fetchBaseQuery({
    baseUrl: `${getApiBaseUrl()}/`,
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.set('X-Client', 'web');
      return headers;
    },
  }),
  endpoints: builder => ({
    getSession: builder.query<ConnectionInfos, void>({
      query: () => ({
        url: 'login',
        method: 'POST',
        body: {},
      }),
      transformResponse: (data: { user: User }) => data.user,
      transformErrorResponse: (response: { status: string | number }) => response.status,
    }),
    requestOtp: builder.mutation<{ emailAddress: string }, OtpRequestInfos>({
      query: body => ({
        url: `auth/otp/request`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: { emailAddress: string }) => data,
    }),
    verifyOtp: builder.mutation<ConnectionInfos, OtpVerifyInfos>({
      query: body => ({
        url: `auth/otp/verify`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: { user: User }) => data.user,
    }),
    loginGoogle: builder.mutation<ConnectionInfos, { credential: string }>({
      query: body => ({
        url: `auth/google/token`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: { user: User }) => data.user,
    }),
    logout: builder.mutation<void, void>({
      query: body => ({
        url: `logout`,
        method: 'POST',
        body,
      }),
      transformResponse: () => undefined,
      invalidatesTags: ['Login'],
    }),
    patchUserMe: builder.mutation<User, Partial<User>>({
      query: body => ({
        url: `users/me`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (data: { user: User }) => data.user,
      invalidatesTags: ['Login'],
    }),
    putUserMePicture: builder.mutation<User, { image: string }>({
      query: body => ({
        url: `users/me/picture`,
        method: 'PUT',
        body,
      }),
      transformResponse: (data: { user: User }) => data.user,
    }),
    deleteUserMePicture: builder.mutation<User, void>({
      query: () => ({
        url: `users/me/picture`,
        method: 'DELETE',
      }),
      transformResponse: (data: { user: User }) => data.user,
    }),
  }),
});

export const {
  useGetSessionQuery,
  useLogoutMutation,
  useRequestOtpMutation,
  useVerifyOtpMutation,
  usePatchUserMeMutation,
  usePutUserMePictureMutation,
  useDeleteUserMePictureMutation,
  useLoginGoogleMutation,
} = apiLogin;
